import { useCallback, useEffect, useMemo, useState } from "react";
import {
  listManufacturingRecipeInventory,
  type MfgRecipeInventoryRow,
} from "@/lib/manufacturingClient";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";

/**
 * Per-plant recipe inventory list. Mirrors `useManufacturingMaterials`:
 * `enabled` short-circuits the fetch when the org doesn't hold the
 * Manufacturing entitlement, and `refresh` re-fetches in working state.
 *
 * The Inventory panel uses this for the "Recipes" SectionCard; the
 * Receive panel uses it to refresh after a cost-entry post.
 */
export function useManufacturingRecipeInventory(opts: { enabled: boolean }) {
  const { enabled } = opts;
  const scopedOrganizationId = useScopedOrganizationId();
  const [rows, setRows] = useState<MfgRecipeInventoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (mode?: { refresh?: boolean }) => {
      if (!enabled) {
        setRows([]);
        setLoading(false);
        setWorking(false);
        return;
      }
      if (mode?.refresh) setWorking(true);
      else setLoading(true);
      setError(null);

      const r = await listManufacturingRecipeInventory();
      if (!r.ok) {
        setRows([]);
        setError(r.error);
        setLoading(false);
        setWorking(false);
        return;
      }
      setRows(Array.isArray(r.data.rows) ? r.data.rows : []);
      setLoading(false);
      setWorking(false);
    },
    [enabled]
  );

  useEffect(() => {
    void load();
  }, [load, scopedOrganizationId]);

  const totalInventoryValue = useMemo(
    () => rows.reduce((sum, r) => sum + (Number(r.unit_cost) || 0) * (Number(r.on_hand_qty) || 0), 0),
    [rows]
  );

  return {
    rows,
    totalInventoryValue,
    loading,
    working,
    error,
    refresh: () => void load({ refresh: true }),
  };
}
